/**
 * useQuantXData - Custom React Hook for QuantX Analytics
 *
 * Provides state management, parallel data fetching, auto-refresh,
 * and real-time SignalR updates for QuantX Analytics data.
 *
 * @see docs/HANDOFF_QuantX_Integration.md for integration details
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
    getCurrentState,
    getStrategySignal,
    getStrategyDashboard,
    getTrendFilterStats,
    getAdxAnalysis,
    getAIContext,
    getFibLevels,       // DEP-1: New Fibonacci levels endpoint
    fetchAllQuantXData
} from '../api/quantxApi';
import useQuantXSignalR from './useQuantXSignalR';

// Default refresh interval (30 seconds)
const DEFAULT_REFRESH_INTERVAL = 30000;

// Minimum refresh interval (5 seconds)
const MIN_REFRESH_INTERVAL = 5000;

/**
 * useQuantXData Hook
 *
 * @param {Object} options - Hook configuration options
 * @param {string} options.symbol - Trading symbol (e.g., 'XAUUSD_ct')
 * @param {string} options.token - JWT authentication token
 * @param {boolean} options.autoRefresh - Enable auto-refresh (default: true)
 * @param {number} options.refreshInterval - Refresh interval in ms (default: 30000)
 * @param {boolean} options.enabled - Enable/disable data fetching (default: true)
 * @param {boolean} options.useSignalR - Enable real-time SignalR updates (default: true)
 *
 * @returns {Object} Hook state and actions
 */
const useQuantXData = ({
    symbol,
    token,
    autoRefresh = true,
    refreshInterval = DEFAULT_REFRESH_INTERVAL,
    enabled = true,
    useSignalR: enableSignalR = true
}) => {
    // State - Core data
    const [data, setData] = useState(null);
    const [currentState, setCurrentState] = useState(null);
    const [strategySignal, setStrategySignal] = useState(null);
    const [strategyDashboard, setStrategyDashboard] = useState(null);

    // State - Extended API data (Task 1.1-1.4)
    const [trendFilterStats, setTrendFilterStats] = useState(null);
    const [adxAnalysis, setAdxAnalysis] = useState(null);
    const [aiContext, setAIContext] = useState(null);
    const [fibLevels, setFibLevels] = useState(null);  // DEP-1: New Fibonacci levels

    // State - Loading and metadata
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetchedAt, setLastFetchedAt] = useState(null);
    const [fetchCount, setFetchCount] = useState(0);

    // Refs for cleanup
    const intervalRef = useRef(null);
    const isMountedRef = useRef(true);

    /**
     * SignalR event handlers for real-time updates
     */
    const handleTrendUpdate = useCallback((data) => {
        if (!isMountedRef.current) return;
        console.log('[useQuantXData] SignalR TrendUpdate:', data);
        // Update current state with real-time trend data
        setCurrentState(prev => prev ? {
            ...prev,
            h4Trend: data.h4Trend || prev.h4Trend,
            d1Trend: data.d1Trend || prev.d1Trend,
            trendScore: data.trendScore ?? prev.trendScore,
            overallTrend: data.overallTrend || prev.overallTrend
        } : data);
    }, []);

    const handleFibLevelAlert = useCallback((data) => {
        if (!isMountedRef.current) return;
        console.log('[useQuantXData] SignalR FibLevelAlert:', data);
        // Update strategy dashboard with Fib alert
        setStrategyDashboard(prev => prev ? {
            ...prev,
            fibonacciLevels: {
                ...prev.fibonacciLevels,
                nearestLevel: data.level || prev.fibonacciLevels?.nearestLevel,
                distanceToNearest: data.distance ?? prev.fibonacciLevels?.distanceToNearest
            }
        } : prev);
    }, []);

    const handleAdxRegimeChange = useCallback((data) => {
        if (!isMountedRef.current) return;
        console.log('[useQuantXData] SignalR AdxRegimeChange:', data);
        // Update ADX analysis
        setCurrentState(prev => prev ? {
            ...prev,
            adx: {
                value: data.value ?? prev.adx?.value,
                range: data.range || prev.adx?.range
            }
        } : prev);
    }, []);

    const handleSessionChange = useCallback((data) => {
        if (!isMountedRef.current) return;
        console.log('[useQuantXData] SignalR SessionChange:', data);
        // Update session info
        setCurrentState(prev => prev ? {
            ...prev,
            session: data.session || prev.session
        } : prev);
    }, []);

    const handleSignalRError = useCallback((err) => {
        console.error('[useQuantXData] SignalR error:', err);
        // Don't set error state for SignalR issues - fall back to polling
    }, []);

    /**
     * SignalR connection for real-time updates
     */
    const signalR = useQuantXSignalR({
        token,
        symbol,
        enabled: enabled && enableSignalR,
        onTrendUpdate: handleTrendUpdate,
        onFibLevelAlert: handleFibLevelAlert,
        onAdxRegimeChange: handleAdxRegimeChange,
        onSessionChange: handleSessionChange,
        onError: handleSignalRError
    });

    /**
     * Fetch all QuantX data in parallel
     * Includes core endpoints + extended endpoints (Task 1.1-1.4)
     */
    const fetchData = useCallback(async () => {
        if (!symbol || !token || !enabled) {
            console.log('[useQuantXData] Skipping fetch - missing symbol, token, or disabled');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Fetch core data + extended endpoints in parallel
            const [
                coreResult,
                trendStats,
                adxData,
                contextData,
                fibData
            ] = await Promise.all([
                fetchAllQuantXData(token, symbol),
                // Task 1.1: Trend filter stats (graceful fallback)
                getTrendFilterStats(token, symbol).catch(err => {
                    console.warn('[useQuantXData] getTrendFilterStats failed:', err.message);
                    return null;
                }),
                // Task 1.2: ADX analysis (graceful fallback) - DEP-2: Now includes D1 data
                getAdxAnalysis(token, symbol).catch(err => {
                    console.warn('[useQuantXData] getAdxAnalysis failed:', err.message);
                    return null;
                }),
                // Task 1.4: AI context (graceful fallback)
                getAIContext(token, symbol).catch(err => {
                    console.warn('[useQuantXData] getAIContext failed:', err.message);
                    return null;
                }),
                // DEP-1: Fibonacci levels (graceful fallback)
                getFibLevels(token, symbol).catch(err => {
                    console.warn('[useQuantXData] getFibLevels failed:', err.message);
                    return null;
                })
            ]);

            // Only update state if component is still mounted
            if (isMountedRef.current) {
                // Core data
                setData(coreResult);
                setCurrentState(coreResult.currentState);
                setStrategySignal(coreResult.strategySignal);
                setStrategyDashboard(coreResult.strategyDashboard);

                // Extended data (Task 1.1-1.4)
                setTrendFilterStats(trendStats);
                setAdxAnalysis(adxData);
                setAIContext(contextData);
                setFibLevels(fibData);  // DEP-1: New Fibonacci levels

                setLastFetchedAt(new Date());
                setFetchCount(prev => prev + 1);
                setLoading(false);

                console.log('[useQuantXData] All endpoints fetched:', {
                    core: !!coreResult,
                    trendStats: !!trendStats,
                    adxAnalysis: !!adxData,
                    aiContext: !!contextData,
                    fibLevels: !!fibData  // DEP-1
                });
            }
        } catch (err) {
            if (isMountedRef.current) {
                console.error('[useQuantXData] Fetch error:', err);
                setError(err);
                setLoading(false);
            }
        }
    }, [symbol, token, enabled]);

    /**
     * Refresh data manually
     */
    const refresh = useCallback(() => {
        fetchData();
    }, [fetchData]);

    /**
     * Clear all data
     */
    const clear = useCallback(() => {
        setData(null);
        setCurrentState(null);
        setStrategySignal(null);
        setStrategyDashboard(null);
        // Extended data (Task 1.1-1.4)
        setTrendFilterStats(null);
        setAdxAnalysis(null);
        setAIContext(null);
        setFibLevels(null);  // DEP-1: Clear Fibonacci levels
        setError(null);
        setLastFetchedAt(null);
    }, []);

    // Initial fetch and auto-refresh setup
    useEffect(() => {
        isMountedRef.current = true;

        // Initial fetch
        if (enabled && symbol && token) {
            fetchData();
        }

        // Setup auto-refresh
        if (autoRefresh && enabled && symbol && token) {
            const interval = Math.max(refreshInterval, MIN_REFRESH_INTERVAL);
            intervalRef.current = setInterval(() => {
                if (isMountedRef.current) {
                    fetchData();
                }
            }, interval);
        }

        // Cleanup
        return () => {
            isMountedRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [symbol, token, autoRefresh, refreshInterval, enabled, fetchData]);

    // Clear data when symbol changes
    useEffect(() => {
        if (symbol) {
            clear();
        }
    }, [symbol, clear]);

    // Computed values
    const isLoading = loading && !data;
    const isRefreshing = loading && data;
    const hasData = !!data;
    const hasError = !!error;

    // Check for insufficient historical data
    // When price is 0 or undefined, and trendScore is 0, the symbol lacks enough bars for EMA calculation
    const hasInsufficientData = data && (
        (!currentState?.price || currentState?.price === 0) &&
        (!currentState?.trendScore || currentState?.trendScore === 0)
    );

    // Extract commonly used values for convenience
    const trendScore = currentState?.trendScore || 0;
    const overallTrend = currentState?.overallTrend || 'UNKNOWN';
    const signal = strategySignal?.signal || 'NEUTRAL';
    const confidence = strategySignal?.confidence || 0;
    const session = currentState?.session || 'UNKNOWN';

    return {
        // Raw data - Core
        data,
        currentState,
        strategySignal,
        strategyDashboard,

        // Raw data - Extended (Task 1.1-1.4)
        trendFilterStats,    // Task 1.1: EMA bars-above analysis
        adxAnalysis,         // Task 1.2: Detailed ADX/ATR data (DEP-2: now includes D1)
        aiContext,           // Task 1.4: Pre-computed AI context
        fibLevels,           // DEP-1: Current Fibonacci swing levels

        // State flags
        loading,
        isLoading,
        isRefreshing,
        hasData,
        hasError,
        hasInsufficientData,
        error,

        // Metadata
        lastFetchedAt,
        fetchCount,
        symbol,

        // Computed values
        trendScore,
        overallTrend,
        signal,
        confidence,
        session,

        // SignalR state
        signalRConnected: signalR.isConnected,
        signalRState: signalR.connectionState,
        signalRLastEvent: signalR.lastEvent,

        // Actions
        refresh,
        clear,
        reconnectSignalR: signalR.reconnect
    };
};

export default useQuantXData;
